class LocalStore {
  constructor(prefix = '') {
    this.prefix = prefix;
    this._cache = new Map();
    this._cacheEnabled = true;
  }

  /**
   * Generates the full key with prefix
   * @param {string} key
   * @returns {string}
   */
  _key(key) {
    if (!key || typeof key !== 'string') {
      console.warn(`LocalStore: Invalid key provided - ${key}`);
      return '';
    }
    return `${this.prefix}${key}`;
  }

  /**
   * Sets a value in localStorage
   * @param {string} key
   * @param {any} value
   */
  set(key, value) {
    const storageKey = this._key(key);
    if (!storageKey) return;

    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(storageKey, serialized);

      if (this._cacheEnabled) {
        this._cache.set(storageKey, value);
      }
    } catch (error) {
      console.error(`LocalStore: Failed to serialize value for key "${key}"`, error);
    }
  }

  /**
   * Gets a value from localStorage
   * @param {string} key
   * @param {any} defaultValue
   * @returns {any}
   */
  get(key, defaultValue = null) {
    const storageKey = this._key(key);
    if (!storageKey) return defaultValue;

    // Check cache first
    if (this._cacheEnabled && this._cache.has(storageKey)) {
      return this._cache.get(storageKey);
    }

    try {
      const item = localStorage.getItem(storageKey);
      if (item === null) return defaultValue;

      const parsed = JSON.parse(item);

      if (this._cacheEnabled) {
        this._cache.set(storageKey, parsed);
      }

      return parsed;
    } catch (error) {
      return defaultValue;
    }
  }

  /**
   * Removes an item from localStorage
   * @param {string} key
   */
  remove(key) {
    const storageKey = this._key(key);
    if (!storageKey) return;

    localStorage.removeItem(storageKey);

    if (this._cacheEnabled) {
      this._cache.delete(storageKey);
    }
  }

  /**
   * Clears all items with the current prefix
   */
  clear() {
    Object.keys(localStorage)
      .filter(k => k.startsWith(this.prefix))
      .forEach(k => {
        localStorage.removeItem(k);
        if (this._cacheEnabled) {
          this._cache.delete(k);
        }
      });
  }

  /**
   * Toggles caching mechanism
   * @param {boolean} enabled
   */
  setCacheEnabled(enabled) {
    this._cacheEnabled = enabled;
    if (!enabled) {
      this._cache.clear();
    }
  }

  /**
   * Gets all keys with the current prefix
   * @returns {string[]}
   */
  keys() {
    return Object.keys(localStorage)
      .filter(k => k.startsWith(this.prefix))
      .map(k => k.slice(this.prefix.length));
  }

  /**
   * Gets the number of items with the current prefix
   * @returns {number}
   */
  size() {
    return Object.keys(localStorage)
      .filter(k => k.startsWith(this.prefix))
      .length;
  }
}

export default LocalStore;
